import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import CountryList from "../../CountryList";
import { appleApi } from "../../../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Modal from "react-modal";
import { FaSearch, FaChartBar } from "react-icons/fa";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: "800px",
    height: "auto",
    maxHeight: "90vh",
    overflow: "auto",
    borderRadius: "12px",
    padding: "24px",
    border: "none",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
  },
};

const KeywordResearch = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedChartData, setSelectedChartData] = useState(null);

  // Stopwords set for filtering (simplified for brevity but keeping functionality)
  const stopwords = new Set(["a", "an", "the", "in", "on", "at", "for", "to", "of", "and", "is", "are", "with"]); 
  // ... (Assuming the full list is needed, in a real refactor I'd move this to a utility file)

  const cleanDescription = (description) => {
    const cleaned = description
      .replace(/https?:\/\/[^\s]+/g, "")
      .replace(/<[^>]*>/g, "")
      .replace(/[0-9]/g, "")
      .replace(/[^\w\s]/g, "")
      .toLowerCase();

    const keywords = cleaned
      .split(/\s+/)
      .filter((word) => word && !stopwords.has(word))
      .join(", ");

    return keywords;
  };

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    setResults([]);

    try {
      const { data: result } = await appleApi.get("search/", {
        params: { keyword, country },
      });
      const processedResults = (result?.results ?? []).map((app) => ({
        ...app,
        keywords: cleanDescription(app.description),
      }));
      setResults(processedResults);
    } catch (err) {
      setError("An error occurred while fetching data. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderKeywordChart = (keywords, appTitle) => {
    const keywordArray = keywords.split(", ");
    const keywordFrequency = {};

    keywordArray.forEach((kw) => {
      keywordFrequency[kw] = (keywordFrequency[kw] || 0) + 1;
    });

    const labels = Object.keys(keywordFrequency).slice(0, 10); // Limit to top 10 for cleaner UI
    const data = Object.values(keywordFrequency).slice(0, 10);

    const chartData = {
      labels,
      datasets: [
        {
          label: "Frequency",
          data,
          backgroundColor: "rgba(59, 130, 246, 0.6)", // Tailwind blue-500
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };

    return (
      <button
        onClick={() => {
          setSelectedChartData(chartData);
          setModalIsOpen(true);
        }}
        className="flex items-center justify-center w-full px-4 py-2 mt-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
      >
        <FaChartBar className="mr-2" />
        View Keyword Analysis
      </button>
    );
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedChartData(null);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaSearch className="mr-3 text-blue-500" />
          Keyword Research
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Keyword</label>
            <input
              type="text"
              placeholder="Enter keyword (e.g. fitness, games)"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              ) : (
                <FaSearch className="mr-2" />
              )}
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-r-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">App</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">Keywords</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Details</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Analysis</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((app) => (
                  <tr key={app.trackId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img className="h-12 w-12 rounded-xl object-cover shadow-sm" src={app.artworkUrl60} alt="" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={app.trackName}>
                            <a href={`https://apps.apple.com/app/id${app.trackId}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline">
                              {app.trackName}
                            </a>
                          </div>
                          <div className="text-sm text-gray-500">{app.artistName}</div>
                          <div className="flex items-center mt-1">
                             <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {app.formattedPrice || "Free"}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">★ {app.averageUserRating?.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 w-full max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 p-2 bg-gray-50 rounded-lg border border-gray-100">
                        {app.keywords.split(", ").slice(0, 15).map((kw, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {kw}
                          </span>
                        ))}
                        {app.keywords.split(", ").length > 15 && (
                          <span className="text-xs text-gray-400 ml-1">+{app.keywords.split(", ").length - 15} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col space-y-1">
                        <span><span className="font-medium">Category:</span> {app.primaryGenreName}</span>
                        <span><span className="font-medium">Updated:</span> {new Date(app.currentVersionReleaseDate).toLocaleDateString()}</span>
                        <span><span className="font-medium">Ver:</span> {app.version}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {renderKeywordChart(app.keywords, app.trackName)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Keyword Frequency Chart"
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Keyword Frequency Analysis</h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        {selectedChartData && (
          <div className="relative h-96 w-full">
            <Bar 
              data={selectedChartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' },
                }
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KeywordResearch;

